import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Grid, Typography, Tabs, Tab, Box } from '@mui/material';
import LCInfo from './LCInfo';
import LCMembers from './LCMembers';
import LCLeadership from './LCLeadership';
import LCResources from './LCResources';
import LCMore from './LCMore';
import { useApiGet } from '../../../utils/httpClientTs';
import { getLocalChapterDetail } from '../../../endpoints/chaptersTs';

type TabType = 'info' | 'leadership' | 'members' | 'resources' | 'more';

const LCDetail: React.FC = () => {
  let { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data } = useApiGet(
    ['lc-chapters-detail', id],
    () => getLocalChapterDetail(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  // Create dynamic tab mapping based on available tabs (More hub links to Committees, Social, Events)
  const getAvailableTabs = useCallback((): TabType[] => {
    const tabs: TabType[] = ['info', 'leadership'];
    if (data?.local_chapter_access) {
      tabs.push('members', 'resources');
      if (data?.app_pilot_program) {
        tabs.push('more');
      }
    }
    return tabs;
  }, [data?.local_chapter_access, data?.app_pilot_program]);

  const convertTextToTabIndex = useCallback(
    (text: string): number => {
      const availableTabs = getAvailableTabs();
      const index = availableTabs.indexOf(text as TabType);
      // If the requested tab is not available, return 0 (info tab)
      return index >= 0 ? index : 0;
    },
    [getAvailableTabs]
  );

  const convertTabIndexToText = useCallback(
    (index: number): TabType => {
      const availableTabs = getAvailableTabs();
      return availableTabs[index] || 'info';
    },
    [getAvailableTabs]
  );

  const [tabSelected, setTabSelected] = useState<number>(0);

  // Sync tab from URL when chapter data or query changes (e.g. deep link, browser back/forward)
  useEffect(() => {
    if (!data) return;
    const urlTab = searchParams.get('tab');
    if (urlTab) {
      setTabSelected(convertTextToTabIndex(urlTab));
    } else {
      setTabSelected(0);
    }
  }, [data, searchParams, convertTextToTabIndex]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabSelected(newValue);
    const tabText = convertTabIndexToText(newValue);
    const next = new URLSearchParams(searchParams);
    if (tabText === 'info') {
      next.delete('tab');
    } else {
      next.set('tab', tabText);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={{ p: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          {data?.is_disbanded ? (
            <Typography variant="h4" color="error">
              {data?.name} (Disbanded)
            </Typography>
          ) : (
            <Typography variant="h4">{data?.name}</Typography>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sx={{ p: 2 }}>
        <Tabs
          value={tabSelected}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {getAvailableTabs().map((tab) => (
            <Tab
              key={tab}
              label={
                tab === 'info'
                  ? 'Info'
                  : tab === 'leadership'
                    ? 'Leadership'
                    : tab === 'members'
                      ? 'Members'
                      : tab === 'resources'
                        ? 'Resources'
                        : tab === 'more'
                          ? 'More'
                          : tab
              }
            />
          ))}
        </Tabs>
      </Grid>
      <Grid item xs={12} sx={{ p: 2 }}>
        {(() => {
          const currentTab = convertTabIndexToText(tabSelected);
          switch (currentTab) {
            case 'info':
              return (
                <LCInfo
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                  isAdmin={data?.is_admin || false}
                  isAdminOrSupport={data?.is_admin_or_support || false}
                  canManage={data?.local_chapter_manage || false}
                />
              );
            case 'leadership':
              return (
                <LCLeadership
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                />
              );
            case 'members':
              return data?.local_chapter_access ? (
                <LCMembers
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                />
              ) : null;
            case 'more':
              return data?.local_chapter_access ? (
                <LCMore
                  id={id!}
                  localChapterAccess={data?.local_chapter_access}
                  appPilotProgram={data?.app_pilot_program}
                />
              ) : null;
            case 'resources':
              return data?.local_chapter_access ? (
                <LCResources
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                />
              ) : null;
            default:
              return <LCInfo id={id!} lcAccess={data?.local_chapter_access} />;
          }
        })()}
      </Grid>
    </Grid>
  );
};

export default LCDetail;
